import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import OptionSelect from '@/pages/apps/components/option-select';
import type { InputValue } from '@/pages/apps/records/components/field-values';
import type { Customer, ListField } from '@/types/platform';
import { Link } from '@inertiajs/react';

interface FieldInputProps {
  field: ListField;
  id: string;
  value: InputValue;
  onChange: (value: InputValue) => void;
  error?: string;
  customers: Customer[];
  customersHref: string;
}

const INPUT_TYPES: Partial<Record<ListField['type'], string>> = {
  text: 'text',
  email: 'email',
  phone: 'tel',
  date: 'date',
};

const RequiredMark = () => {
  return (
    <span className="text-destructive" aria-hidden="true">
      *
    </span>
  );
};

// One field of a record: its label, the input its type calls for, and its problem if it has one.
const FieldInput = ({
  field,
  id,
  value,
  onChange,
  error,
  customers,
  customersHref,
}: FieldInputProps) => {
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : undefined;
  const invalid = error ? true : undefined;
  const text = typeof value === 'string' ? value : '';
  const problem = error ? (
    <p id={errorId} className="text-destructive text-sm">
      {error}
    </p>
  ) : null;
  const label = (
    <Label htmlFor={id}>
      {field.label}
      {field.required ? <RequiredMark /> : null}
    </Label>
  );

  if (field.type === 'checkbox') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id={id}
            checked={value === true}
            onCheckedChange={(checked) => onChange(checked === true)}
            aria-invalid={invalid}
            aria-describedby={describedBy}
          />
          <Label htmlFor={id}>{field.label}</Label>
        </div>
        {problem}
      </div>
    );
  }

  if (field.type === 'location') {
    const point = typeof value === 'object' ? value : { lat: '', lng: '' };

    return (
      <fieldset className="space-y-2" aria-describedby={describedBy}>
        <legend className="text-sm leading-none font-medium">
          {field.label} {field.required ? <RequiredMark /> : null}
        </legend>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor={`${id}-lat`} className="text-muted-foreground text-xs">
              Latitude
            </Label>
            <Input
              id={`${id}-lat`}
              type="number"
              inputMode="decimal"
              step="any"
              min={-90}
              max={90}
              value={point.lat}
              onChange={(event) => onChange({ ...point, lat: event.target.value })}
              aria-invalid={invalid}
              aria-required={field.required || undefined}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${id}-lng`} className="text-muted-foreground text-xs">
              Longitude
            </Label>
            <Input
              id={`${id}-lng`}
              type="number"
              inputMode="decimal"
              step="any"
              min={-180}
              max={180}
              value={point.lng}
              onChange={(event) => onChange({ ...point, lng: event.target.value })}
              aria-invalid={invalid}
              aria-required={field.required || undefined}
            />
          </div>
        </div>
        {problem}
      </fieldset>
    );
  }

  if (field.type === 'choice') {
    return (
      <div className="space-y-2">
        {label}
        <OptionSelect
          id={id}
          value={text}
          onChange={onChange}
          options={field.options.map((option) => ({ value: option, label: option }))}
          emptyLabel={field.required ? undefined : 'None'}
          required={field.required}
          invalid={Boolean(error)}
          describedBy={describedBy}
        />
        {problem}
      </div>
    );
  }

  if (field.type === 'customer') {
    return (
      <div className="space-y-2">
        {label}
        <OptionSelect
          id={id}
          value={text}
          onChange={onChange}
          options={customers.map((customer) => ({ value: customer.id, label: customer.name }))}
          placeholder={customers.length === 0 ? 'No customers yet' : 'Choose a customer'}
          emptyLabel={field.required ? undefined : 'No customer'}
          required={field.required}
          invalid={Boolean(error)}
          describedBy={describedBy}
        />
        {problem}
        <p className="text-sm">
          <Link href={customersHref} className="font-medium underline">
            Add a customer
          </Link>
        </p>
      </div>
    );
  }

  if (field.type === 'long_text') {
    return (
      <div className="space-y-2">
        {label}
        <Textarea
          id={id}
          rows={4}
          value={text}
          onChange={(event) => onChange(event.target.value)}
          required={field.required}
          aria-invalid={invalid}
          aria-describedby={describedBy}
        />
        {problem}
      </div>
    );
  }

  if (field.type === 'money') {
    return (
      <div className="space-y-2">
        {label}
        <div className="relative">
          <span
            className="text-muted-foreground pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm"
            aria-hidden="true"
          >
            $
          </span>
          <Input
            id={id}
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            className="pl-7"
            value={text}
            onChange={(event) => onChange(event.target.value)}
            required={field.required}
            aria-invalid={invalid}
            aria-describedby={describedBy}
          />
        </div>
        {problem}
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <div className="space-y-2">
        {label}
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          step="any"
          value={text}
          onChange={(event) => onChange(event.target.value)}
          required={field.required}
          aria-invalid={invalid}
          aria-describedby={describedBy}
        />
        {problem}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label}
      <Input
        id={id}
        type={INPUT_TYPES[field.type] ?? 'text'}
        value={text}
        onChange={(event) => onChange(event.target.value)}
        required={field.required}
        aria-invalid={invalid}
        aria-describedby={describedBy}
      />
      {problem}
    </div>
  );
};

export default FieldInput;
